import React, { useEffect, useState } from "react";
import {
  Modal,
  ModalBody,
  Button,
  Input,
  Table,
  Form,
  Label,
  Row,
  Col
} from "reactstrap";
import { toast } from "react-toastify";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { addQuestionType } from "model/QuestionTypeModel";

const sortDetailsBySortField = (details = []) =>
  [...(details || [])].sort((a, b) => {
    const sortA = Number(a?.sort ?? a?.sortOrder ?? 0);
    const sortB = Number(b?.sort ?? b?.sortOrder ?? 0);
    return sortA - sortB;
  });

const createEmptyRow = () => ({
  id: null,
  question: "",
  expectedAnswer: "",
  forFunnelJob: false,
  isMendatory: false,
  status: true,
  sortOrder: 0,
  sort: 0,
});

const initialFormState = {
  name: "",
  status: true,
  questions: [],
};

const mapQuestionTypeDetailsToFormData = (questionTypeDetails = {}) => ({
  name: questionTypeDetails?.name || questionTypeDetails?.questionTypeName || "",
  status: questionTypeDetails?.status === "active",
  questions: sortDetailsBySortField(questionTypeDetails?.details).map((questionItem, index) => ({
    id: questionItem.id,
    question: questionItem?.name || questionItem?.detailsName || "",
    expectedAnswer: questionItem?.expectedAnswer ?? "",
    status: questionItem?.status === "active",
    isMendatory: questionItem?.isMendatory ?? false,
    sortOrder: Number(questionItem?.sortOrder ?? questionItem?.sort ?? index ?? 0),
    sort: Number(questionItem?.sort ?? questionItem?.sortOrder ?? index ?? 0),
  })),
});

const QuestionTypeAddEditModal = ({
  questionTypeAddEditModalOpen,
  setQuestionTypeAddEditModalOpen,
  itemId,
  setItemId,
  getQuestionTypeList,
  formData,
  setFormData,
  questionTypeDetails
}) => {
  const [formKey, setFormKey] = useState(0);

  const closeModal = () => {
    setQuestionTypeAddEditModalOpen(false);
    setFormData(initialFormState);
    setItemId?.(null);
    setFormKey((prev) => prev + 1);
  };

  const handleRootChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  /* ---------------- Add Row ---------------- */
  const handleAddRow = () => {
    setFormData(prev => ({
      ...prev,
      questions: [
        ...(prev.questions || []),
        {
          ...createEmptyRow(),
          sortOrder: (prev.questions || []).length,
          sort: (prev.questions || []).length,
        },
      ],
    }));
  };

  const handleDeleteRow = (index) => {
    setFormData(prev => {
      const nextQuestions = (prev.questions || [])
        .filter((_, i) => i !== index)
        .map((row, idx) => ({
          ...row,
          sortOrder: idx,
          sort: idx,
        }));
      return {
        ...prev,
        questions: nextQuestions,
      };
    });
  };

  const handleDragEnd = (result) => {
    if (!result?.destination) return;
    if (result.destination.index === result.source.index) return;

    setFormData((prev) => {
      const currentQuestions = [...(prev?.questions || [])];
      const [movedRow] = currentQuestions.splice(result.source.index, 1);
      currentQuestions.splice(result.destination.index, 0, movedRow);
      const reorderedQuestions = currentQuestions.map((row, idx) => ({
        ...row,
        sortOrder: idx,
        sort: idx,
      }));
      return {
        ...prev,
        questions: reorderedQuestions,
      };
    });
  };

  const handleQuestionChange = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      questions: prev.questions.map((row, i) =>
        i === index ? { ...row, [field]: value } : row
      ),
    }));
  };


  const handleSubmit = async () => {
    if (!formData?.name?.trim()) {
      toast.error("Question Type is required");
      return;
    }

    if (!formData?.questions?.length) {
      toast.error("Please add at least one question");
      return;
    }

    if (formData?.questions?.some(q => !q.question.trim())) {
      toast.error("Question field is required");
      return;
    }

    const params = {
      id: questionTypeDetails?.id || null,
      name: formData?.name,
      status: formData?.status ? 'active' : 'deactive',
      details: formData?.questions?.map((innerQuestionItem, index) => ({
        id: itemId ? innerQuestionItem?.id : null,
        name: innerQuestionItem.question,
        status: innerQuestionItem?.status ? 'active' : 'deactive',
        expectedAnswer: innerQuestionItem.expectedAnswer || "",
        isMendatory: innerQuestionItem.isMendatory,
        sortOrder: Number(innerQuestionItem?.sortOrder ?? innerQuestionItem?.sort ?? index ?? 0),
      }))
    };

    let response;
    response = await addQuestionType(params);
    if (response?.success) {
      toast.success(response?.message);
      closeModal();
      getQuestionTypeList?.();
    } else {
      toast.error(response?.message);
    }
  };


  /* ---------------- Edit Mode ---------------- */
  useEffect(() => {
    if (!questionTypeAddEditModalOpen) return;

    if (itemId && questionTypeDetails?.details) {
      setFormData(mapQuestionTypeDetailsToFormData(questionTypeDetails));
      setFormKey((prev) => prev + 1);
    }
  }, [questionTypeAddEditModalOpen, itemId, questionTypeDetails]);


  /* ---------------- Auto add first row ---------------- */
  useEffect(() => {
    if (questionTypeAddEditModalOpen && !itemId && !formData.questions?.length) {
      handleAddRow();
      setFormKey((prev) => prev + 1);
    }
  }, [questionTypeAddEditModalOpen, itemId]);

  return (
    <Modal
      isOpen={questionTypeAddEditModalOpen}
      toggle={closeModal}
      backdrop="static"
      size="xl"
      centered
      zIndex={5000}
      className="modal-full-height question-type-modal"
    >
      <ModalBody className="p-4" style={{ maxHeight: "95vh", overflowY: "auto" }}>
        <Form
          key={formKey}
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          {/* Header */}
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="fw-bold mb-0">
              {itemId ? "Edit" : "Create"} Question Type
            </h5>
            <Button close onClick={closeModal} />
          </div>

          <hr />
          <Row className="justify-content-between align-items-center">
            <Col md={8}>
              <Label>Question Type <span className="text-danger">*</span></Label>
              <Input
                type="text"
                name="name"
                value={formData.name || ""}
                onChange={e =>
                  handleRootChange("name", e.target.value)
                }
                placeholder="Enter Question Type"
              />
            </Col>
            <Col md={4}>
              <Label>Status</Label>
              <div className="input-box d-flex align-items-center justify-content-between">
                <span className="text-muted">Choose Status</span>
                <div className="d-flex align-items-center">
                  <span className={`me-2 fw-semibold ${formData?.status ? 'text-success' : ''}`}>
                    {formData?.status ? "Active" : "Deactive"}
                  </span>
                  <div className="form-switch">
                    <Input
                      type="switch"
                      name="status"
                      defaultChecked={formData?.status}
                      onChange={e =>
                        handleRootChange(
                          "status",
                          e.target.checked
                        )
                      }
                      className="form-check-input-padding"
                    />
                  </div>
                </div>
              </div>
            </Col>
          </Row>
          <div className="text-end mt-3">
            <Button
              color="primary"
              type="button"
              onClick={handleAddRow}
            >
              + Add
            </Button>
          </div>
          <DragDropContext onDragEnd={handleDragEnd}>
            <Table responsive bordered className="mt-3">
              <thead>
                <tr>
                  <th style={{ width: "70px" }}>#</th>
                  <th>Question <span className="text-danger">*</span> </th>
                  <th>Expected Answer</th>
                  <th className="text-center col-mandatory">Is Mandatory</th>
                  <th className="text-center col-status">Status</th>
                  <th className="text-center col-actions">Actions</th>
                </tr>
              </thead>
              <Droppable droppableId="question-type-rows" type="QUESTION">
                {(provided) => (
                  <tbody ref={provided.innerRef} {...provided.droppableProps}>
                    {formData?.questions?.map((row, index) => (
                      <Draggable
                        key={`question-type-${row?.id || "new"}-${index}`}
                        draggableId={`question-type-${row?.id || "new"}-${index}`}
                        index={index}
                      >
                        {(providedDraggable) => (
                          <tr
                            ref={providedDraggable.innerRef}
                            {...providedDraggable.draggableProps}
                            key={`question-type-row-${index}`}
                          >
                            <td className="align-middle">
                              <div className="d-flex align-items-center gap-2">
                                <span {...providedDraggable.dragHandleProps}>
                                  <i className="fa fa-grip-vertical cursor-pointer"></i>
                                </span>
                                <span>{index + 1}</span>
                              </div>
                            </td>

                            <td>
                              <Input
                                type="textarea"
                                value={row.question}
                                onChange={e =>
                                  handleQuestionChange(index, "question", e.target.value)
                                }
                                placeholder="Enter Question"
                                rows="2"
                              />
                            </td>

                            <td>
                              <Input
                                type="textarea"
                                value={row.expectedAnswer}
                                onChange={e =>
                                  handleQuestionChange(
                                    index,
                                    "expectedAnswer",
                                    e.target.value
                                  )
                                }
                                placeholder="Enter Expected Answer"
                                rows="2"
                              />
                            </td>
                            <td className="col-mandatory text-center vertical-allign-middle">
                              <Input
                                type="checkbox"
                                defaultChecked={row.isMendatory}
                                onChange={e =>
                                  handleQuestionChange(
                                    index,
                                    "isMendatory",
                                    e.target.checked
                                  )
                                }
                              />
                            </td>
                            <td className="col-status text-center vertical-allign-middle w-110">
                              <div
                                className="d-flex align-items-center justify-content-center"
                              >
                                <span className={`me-2 fw-semibold ${row?.status ? 'text-success' : ''}`}>
                                  {row?.status ? "Active" : "Deactive"}
                                </span>
                                <div className="form-switch">
                                  <Input
                                    type="switch"
                                    name="status"
                                    defaultChecked={row?.status}
                                    onChange={e =>
                                      handleQuestionChange(
                                        index,
                                        "status",
                                        e.target.checked
                                      )
                                    }
                                    className="form-check-input-padding"
                                  />
                                </div>
                              </div>
                            </td>

                            <td className="col-actions text-center vertical-allign-middle">
                              <i
                                className={`mdi mdi-trash-can font-size-18 ${
                                  formData?.questions?.length > 1
                                    ? "text-danger cursor-pointer"
                                    : "text-muted"
                                }`}
                                style={
                                  formData?.questions?.length <= 1
                                    ? { opacity: 0.5, cursor: "not-allowed" }
                                    : undefined
                                }
                                onClick={
                                  formData?.questions?.length > 1
                                    ? () => handleDeleteRow(index)
                                    : undefined
                                }
                              />
                            </td>
                          </tr>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}

                    {!formData.questions?.length && (
                      <tr>
                        <td colSpan="6" className="text-center text-muted">
                          No questions added
                        </td>
                      </tr>
                    )}
                  </tbody>
                )}
              </Droppable>
            </Table>
          </DragDropContext>

          {/* Footer */}
          <div className="text-end mt-3">
            <Button color="primary" type="submit">
              {itemId ? "Update" : "Save"}
            </Button>
          </div>
        </Form>
      </ModalBody>
    </Modal>
  );
};

export default QuestionTypeAddEditModal;
